import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Users, Palette, Globe, Heart, Trophy, Sparkles, Target } from 'lucide-react';
import { SEO } from '../components/SEO';

const AboutPage = () => {
  const stats = [
    { value: '10K+', label: 'Comics' },
    { value: '50K+', label: 'Creators' },
    { value: '1M+', label: 'Readers' },
    { value: '$500K+', label: 'Paid to Creators' },
  ];

  const values = [
    {
      icon: Users,
      title: 'Community First',
      description: 'We prioritize our community of creators and readers, fostering an environment of support and collaboration.',
      color: 'primary'
    },
    {
      icon: Palette,
      title: 'Creative Freedom',
      description: 'Artists have complete creative control over their work, with AI tools to help them succeed on their own terms.',
      color: 'secondary'
    },
    {
      icon: Globe,
      title: 'Global Reach',
      description: 'We connect creators with readers worldwide, breaking down barriers in the comic industry.',
      color: 'primary'
    },
    {
      icon: Heart,
      title: 'Fair Compensation',
      description: 'Creators keep 100% of tips and chapter sales. Our smart revenue sharing rewards engagement.',
      color: 'secondary'
    }
  ];

  const team = [
    { name: 'Alex Chen', role: 'CEO & Founder', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200' },
    { name: 'Sarah Kim', role: 'Head of Product', image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200' },
    { name: 'Marcus Johnson', role: 'Lead Developer', image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200' },
    { name: 'Emily Rodriguez', role: 'Community Manager', image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200' },
  ];

  return (
    <div className="min-h-screen bg-[var(--background)]" data-testid="about-page">
      <SEO title="About Us" description="Learn about Supremics - the ultimate platform for comic creators and readers." />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-[var(--surface)] to-[var(--background)] border-b border-[var(--border)]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[var(--primary)]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[var(--secondary)]/5 rounded-full blur-3xl" />
        </div>
        <div className="relative page-padding py-20 text-center">
          <p className="text-xs tracking-[0.3em] text-[var(--primary)] uppercase mb-4">About Us</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-teko font-semibold mb-6">
            Empowering Comic<br />
            <span className="text-gradient">Creators Worldwide</span>
          </h1>
          <p className="text-[var(--text-secondary)] max-w-2xl mx-auto text-lg">
            Supremics is dedicated to creating a vibrant community where comic creators 
            and readers can connect, share, and celebrate the art of visual storytelling.
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <div className="page-padding -mt-12 relative z-10">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <div key={index} className="bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-6 text-center">
              <p className="text-3xl md:text-4xl font-teko text-[var(--primary)]">{stat.value}</p>
              <p className="text-sm text-[var(--text-secondary)]">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Mission Section */}
      <div className="page-padding py-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Target className="text-[var(--primary)]" size={32} />
                <h2 className="text-3xl font-teko">Our Mission</h2>
              </div>
              <p className="text-[var(--text-secondary)] leading-relaxed mb-6">
                We believe everyone has a story worth telling. Supremics was built to 
                give independent comic creators the tools, audience, and revenue they 
                deserve—without the gatekeepers of traditional publishing.
              </p>
              <p className="text-[var(--text-secondary)] leading-relaxed">
                From AI-powered coloring and translation tools to smart revenue sharing 
                and weekly prizes, we're constantly innovating to help creators succeed.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[var(--primary)]/20 to-[var(--secondary)]/20 rounded-3xl blur-xl" />
              <div className="relative bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-8">
                <Sparkles className="text-[var(--primary)] mb-4" size={40} />
                <h3 className="text-2xl font-teko mb-4">Founded in 2025</h3>
                <p className="text-[var(--text-secondary)]">
                  Supremics emerged from a simple observation: talented comic creators 
                  struggled to find their audience, while readers craved fresh, diverse content. 
                  We set out to bridge this gap.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="page-padding py-16 bg-[var(--surface)]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] text-[var(--primary)] uppercase mb-2">What We Stand For</p>
            <h2 className="text-4xl font-teko">Our Core Values</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={index} 
                className="bg-[var(--background)] border border-[var(--border)] rounded-2xl p-6 hover:border-[var(--primary)]/50 transition-colors"
              >
                <value.icon 
                  className={`mb-4 ${value.color === 'primary' ? 'text-[var(--primary)]' : 'text-[var(--secondary)]'}`} 
                  size={32} 
                />
                <h3 className="font-teko text-xl mb-2">{value.title}</h3>
                <p className="text-sm text-[var(--text-secondary)]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="page-padding py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] text-[var(--primary)] uppercase mb-2">The People Behind Supremics</p>
            <h2 className="text-4xl font-teko">Our Team</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {team.map((member, index) => (
              <div key={index} className="text-center group">
                <div className="relative mb-4 mx-auto w-32 h-32 rounded-full overflow-hidden border-2 border-[var(--border)] group-hover:border-[var(--primary)] transition-colors">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="font-teko text-lg">{member.name}</h3>
                <p className="text-sm text-[var(--text-secondary)]">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="page-padding py-16">
        <div className="max-w-4xl mx-auto">
          <div className="relative bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] rounded-3xl p-12 text-center text-white overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            <div className="relative z-10">
              <Trophy className="mx-auto mb-4" size={48} />
              <h2 className="text-4xl font-teko mb-4">Join Our Community</h2>
              <p className="text-white/80 mb-8 max-w-xl mx-auto">
                Whether you're a reader looking for your next favorite series or 
                a creator ready to share your vision with the world, Supremics is here for you.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/browse" className="px-8 py-3 bg-white text-[var(--primary)] rounded-full font-medium hover:bg-white/90 transition-colors">
                  Browse Comics
                </Link>
                <Link to="/signup" className="px-8 py-3 border-2 border-white rounded-full font-medium hover:bg-white/10 transition-colors">
                  Create Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
